// Copyright (c) Ping. All rights reserved.

import { useCallback, useEffect, useRef, useState } from 'react';
import { log } from '../../log/log';
import type {
  ActivityFeedState,
  ActivityItem,
  ActivityKind,
  ActivityLoader,
  ActivitySort,
  ActivitySubscriber,
} from './types';

export interface UseActivityFeedViewModelArgs {
  readonly load: ActivityLoader;
  readonly subscribe: ActivitySubscriber;
  /** Override only for tests; production should always use the default 80ms. */
  readonly debounceMs?: number;
}

export interface ActivityFeedViewModel {
  readonly state: ActivityFeedState;
  readonly filter: ActivityKind;
  readonly sort: ActivitySort;
  readonly setFilter: (kind: ActivityKind) => void;
  readonly setSort: (sort: ActivitySort) => void;
  readonly retry: () => void;
}

const DEFAULT_DEBOUNCE_MS = 80;

/**
 * Owns the activity-feed state machine. Filter changes are debounced (the
 * cross-platform contract is 80ms); sort changes refetch immediately so the
 * user sees the order rearrange without lag.
 */
export function useActivityFeedViewModel(
  args: UseActivityFeedViewModelArgs,
): ActivityFeedViewModel {
  const { load, subscribe } = args;
  const debounceMs = args.debounceMs ?? DEFAULT_DEBOUNCE_MS;

  const [filter, setFilterRaw] = useState<ActivityKind>('all');
  const [sort, setSort] = useState<ActivitySort>('mostRecent');
  const [state, setState] = useState<ActivityFeedState>({ kind: 'loading' });
  const [retryNonce, setRetryNonce] = useState(0);
  const debounceHandle = useRef<ReturnType<typeof setTimeout> | null>(null);

  // The committed filter is what gets passed to the loader / subscriber.
  // The raw `filter` updates synchronously so the chip UI feels responsive,
  // but commit waits `debounceMs` to coalesce rapid changes.
  const [committedFilter, setCommittedFilter] = useState<ActivityKind>('all');

  useEffect(() => {
    if (debounceHandle.current !== null) {
      clearTimeout(debounceHandle.current);
    }
    debounceHandle.current = setTimeout(() => {
      setCommittedFilter(filter);
    }, debounceMs);
    return () => {
      if (debounceHandle.current !== null) {
        clearTimeout(debounceHandle.current);
      }
    };
  }, [filter, debounceMs]);

  useEffect(() => {
    let cancelled = false;
    let unsubscribe: (() => void) | null = null;

    const apply = (incoming: readonly ActivityItem[]): void => {
      if (cancelled) {
        return;
      }
      if (incoming.length === 0) {
        setState({ kind: 'empty', filter: committedFilter });
        return;
      }
      setState({
        kind: 'populated',
        items: incoming,
        filter: committedFilter,
        sort,
      });
    };

    setState({ kind: 'loading' });

    // `retryNonce` participates in the dep array purely as a re-trigger;
    // referencing it here makes the dependency relationship explicit.
    void retryNonce;

    const bootstrap = async (): Promise<void> => {
      try {
        log.debug('activity.bootstrap', { filter: committedFilter, sort });
        unsubscribe = subscribe(committedFilter, sort, apply);
        const initial = await load(committedFilter, sort);
        apply(initial);
      } catch (error) {
        if (cancelled) {
          return;
        }
        log.error('activity.bootstrap.failed', error as Error, {
          filter: committedFilter,
          sort,
        });
        setState({ kind: 'error', error: error as Error });
      }
    };

    void bootstrap();

    return () => {
      cancelled = true;
      if (unsubscribe !== null) {
        unsubscribe();
      }
    };
  }, [committedFilter, sort, load, subscribe, retryNonce]);

  const setFilter = useCallback((kind: ActivityKind) => {
    setFilterRaw(kind);
  }, []);

  const retry = useCallback(() => {
    setRetryNonce((n) => n + 1);
  }, []);

  return {
    state,
    filter,
    sort,
    setFilter,
    setSort,
    retry,
  };
}
